// The alerts-page group filter: which tracker groups a user receives alert
// notifications for. Stored as a JSON *string* in the `notify_groups` user
// setting, because `user_settings.value` is a text column. gpx-engine reads
// the same shape at delivery.
export const NOTIFY_GROUPS_CODE = 'notify_groups';

export interface NotifyGroupsFilter {
    all: boolean;
    groups: number[];
    ungrouped: boolean;
}

const EVERYTHING: NotifyGroupsFilter = { all: true, groups: [], ungrouped: true };

// Unlike every other alert setting, a missing row means "everything" — existing
// users must keep receiving what they receive today. A row that is present is
// read exactly as gpx-engine reads it, so the form never shows a selection
// delivery would not honour.
export function parseNotifyGroups(raw: unknown): NotifyGroupsFilter {
    let value = raw;

    if (typeof value === 'string') {
        try {
            value = JSON.parse(value);
        } catch {
            return { ...EVERYTHING };
        }
    }

    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return { ...EVERYTHING };
    }

    const stored = value as Record<string, unknown>;

    return {
        all: Boolean(stored.all ?? false),
        groups: Array.isArray(stored.groups)
            ? stored.groups.map(Number).filter(Number.isInteger)
            : [],
        ungrouped: Boolean(stored.ungrouped ?? false),
    };
}

// Which groups show as checked. Stored ids that no longer resolve drop out, and
// a filter that has never been narrowed starts with everything checked.
export function notifyGroupsSelection(
    filter: NotifyGroupsFilter,
    visibleGroupIds: number[],
): number[] {
    if (filter.all && filter.groups.length === 0) {
        return [...visibleGroupIds];
    }

    return visibleGroupIds.filter((id) => filter.groups.includes(id));
}

// While "all devices" is on, `groups` is remembered UI state only — delivery
// short-circuits on `all`. Storing an untouched selection as empty is what
// keeps groups created later checked the next time the list is expanded.
export function serializeNotifyGroups(
    filter: NotifyGroupsFilter,
    visibleGroupIds: number[],
): string {
    const untouched = filter.all && filter.groups.length === visibleGroupIds.length;

    return JSON.stringify({
        all: filter.all,
        groups: untouched ? [] : filter.groups,
        ungrouped: filter.ungrouped,
    });
}
